import React, { useState, useRef, useImperativeHandle, useCallback, forwardRef } from 'react';
import {
  WebcamCapture,
  WebcamCaptureQR,
  WebcamCaptureHybrid,
  type WebcamCaptureHandle,
} from './WebcamCapture';
import WebcamPlaceholder from './WebcamPlaceholder';
import { flashIfScanSucceeded, type ScanCaptureHandler } from './scanFlash';

interface ScannerSectionProps {
  mode?: 'barcode' | 'qr' | 'hybrid';
  onCapture: ScanCaptureHandler;
  cameraOffLabel?: string;
  placeholderChar?: string;
  className?: string;
  buttonClassName?: string;
  webcamOn?: boolean;
  setWebcamOn?: (on: boolean) => void;
  /** Initial camera state for uncontrolled mode (ignored when controlled). */
  defaultWebcamOn?: boolean;
  overlay?: React.ReactNode;
  /** Control pinned to the top-right corner of the live preview (e.g. close camera). */
  cornerAction?: React.ReactNode;
  /** QR mode only. Hides the detection crosshairs. */
  hideReticle?: boolean;
}

export interface ScannerSectionHandle {
  capture: () => void;
}

export const ScannerSection = forwardRef<ScannerSectionHandle, ScannerSectionProps>(
  function ScannerSection(
    {
      mode = 'barcode',
      onCapture,
      cameraOffLabel = 'Turn Off Camera',
      placeholderChar,
      className = '',
      buttonClassName = 'scanner-camera-button',
      webcamOn: controlledWebcamOn,
      setWebcamOn: controlledSetWebcamOn,
      defaultWebcamOn = false,
      overlay,
      cornerAction,
      hideReticle = false,
    },
    ref,
  ): React.ReactElement {
    const [internalWebcamOn, setInternalWebcamOn] = useState(defaultWebcamOn);
    const isControlled = controlledSetWebcamOn != null;
    const webcamOn = isControlled ? controlledWebcamOn : internalWebcamOn;
    const setWebcamOn = isControlled ? controlledSetWebcamOn! : setInternalWebcamOn;
    const cameraRef = useRef<WebcamCaptureHandle>(null);
    const Camera = mode === 'qr' ? WebcamCaptureQR : mode === 'hybrid' ? WebcamCaptureHybrid : WebcamCapture;
    const handleCameraCapture = useCallback((data: string | null): void => {
      void flashIfScanSucceeded(onCapture(data), () => {
        cameraRef.current?.flash();
      });
    }, [onCapture]);

    useImperativeHandle(
      ref,
      () => ({
        capture: (): void => {
          cameraRef.current?.capture();
        },
      }),
      [],
    );

    const content = webcamOn ? (
      <div className="scanner-section-camera">
        <Camera
          ref={cameraRef}
          onCapture={handleCameraCapture}
          overlay={overlay}
          cornerAction={cornerAction}
          hideReticle={hideReticle}
        />
        {!isControlled && (
          <button type="button" className={buttonClassName} onClick={() => setWebcamOn(false)}>
            {cameraOffLabel}
          </button>
        )}
      </div>
    ) : (
      <WebcamPlaceholder onActivate={() => setWebcamOn(true)} mode={mode} placeholderChar={placeholderChar} />
    );

    if (className) {
      return <div className={className}>{content}</div>;
    }
    return content;
  },
);

export default ScannerSection;
